// Keep 1:1 with `PostalCodeValidator.php` in `packages/p2bundle`.
type PostalCodeRule = {
  pattern: RegExp
  examples: readonly string[]
  separator?: string
  separatorPosition?: number
}

export class PostalCodeValidator {
  static normalize(
    postalCode: string | null | undefined,
    countryIso2: string | null,
  ): string | null {
    if (postalCode === null || postalCode === undefined) {
      return null
    }

    const trimmed = postalCode.trim()
    const collapsed = trimmed.replace(/\s+/g, ' ')
    const uppercased = collapsed.toUpperCase()

    if (uppercased === '') {
      return null
    }

    const rule = PostalCodeValidator.findRule(countryIso2)

    return rule === null ? uppercased : PostalCodeValidator.addSeparator(uppercased, rule)
  }

  static isValid(postalCode: string | null | undefined, countryIso2: string | null): boolean {
    const normalized = PostalCodeValidator.normalize(postalCode, countryIso2)
    const rule = PostalCodeValidator.findRule(countryIso2)

    if (normalized === null || rule === null) {
      return true
    }

    return rule.pattern.test(normalized)
  }

  static getExamples(countryIso2: string | null): readonly string[] {
    const rule = PostalCodeValidator.findRule(countryIso2)

    return rule === null ? [] : rule.examples
  }

  private static addSeparator(postalCode: string, rule: PostalCodeRule): string {
    if (
      rule.separator === undefined ||
      rule.separatorPosition === undefined ||
      rule.pattern.test(postalCode)
    ) {
      return postalCode
    }

    const compact = postalCode.replace(/[ -]/g, '')
    const head = compact.slice(0, rule.separatorPosition)
    const tail = compact.slice(rule.separatorPosition)
    const separated = head + rule.separator + tail

    return rule.pattern.test(separated) ? separated : postalCode
  }

  private static findRule(countryIso2: string | null): PostalCodeRule | null {
    return countryIso2 === null ? null : PostalCodeValidator.RULES[countryIso2] ?? null
  }

  private static readonly RULES: Readonly<Record<string, PostalCodeRule>> = {
    AE: { pattern: /^\d*$/, examples: ['12345'] },
    AT: { pattern: /^[1-9]\d{3}$/, examples: ['1010', '3741'] },
    AU: { pattern: /^\d{4}$/, examples: ['2060', '3171'] },
    BE: { pattern: /^\d{4}$/, examples: ['1000', '4000'] },
    BG: { pattern: /^\d{4}$/, examples: ['1000', '1700'] },
    BR: {
      pattern: /^\d{5}-\d{3}$/,
      examples: ['40301-110', '70002-900'],
      separator: '-',
      separatorPosition: 5,
    },
    CA: {
      pattern: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] \d[ABCEGHJ-NPRSTV-Z]\d$/,
      examples: ['H3Z 2Y7', 'V8X 3X4'],
      separator: ' ',
      separatorPosition: 3,
    },
    CH: { pattern: /^\d{4}$/, examples: ['3030', '1211'] },
    CN: { pattern: /^\d{6}$/, examples: ['100808', '266033'] },
    CZ: { pattern: /^[1-7]\d{2} ?\d{2}$/, examples: ['110 00', '11000'] },
    DE: { pattern: /^\d{5}$/, examples: ['26133', '53225'] },
    DK: { pattern: /^\d{4}$/, examples: ['1566', '8660'] },
    ES: { pattern: /^\d{5}$/, examples: ['28039', '08001'] },
    FI: { pattern: /^\d{5}$/, examples: ['00550', '33100'] },
    FR: { pattern: /^\d{5}$/, examples: ['75001', '33380'] },
    GB: {
      pattern:
        /^(?:AB|AL|B|BA|BB|BD|BF|BH|BL|BN|BR|BS|BT|BX|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}$/,
      examples: ['EC1Y 8SY', 'M2 5BQ'],
    },
    GR: {
      pattern: /^\d{3} \d{2}$/,
      examples: ['151 24', '101 88'],
      separator: ' ',
      separatorPosition: 3,
    },
    HR: { pattern: /^[1-9]\d{4}$/, examples: ['10000', '21000'] },
    HU: { pattern: /^[1-9]\d{3}$/, examples: ['1037', '2380'] },
    ID: { pattern: /^[1-9]\d{4}$/, examples: ['40115', '10110'] },
    IE: { pattern: /^[\dA-Z]{3} ?[\dA-Z]{4}$/, examples: ['A65 F4E2', 'D02 X285'] },
    IL: { pattern: /^\d{7}$/, examples: ['9614303', '6100101'] },
    IN: { pattern: /^\d{6}$/, examples: ['110001', '400001'] },
    IT: { pattern: /^\d{5}$/, examples: ['00144', '20121'] },
    JP: {
      pattern: /^\d{3}-\d{4}$/,
      examples: ['154-0023', '100-0001'],
      separator: '-',
      separatorPosition: 3,
    },
    KR: { pattern: /^\d{5}$/, examples: ['03051', '06236'] },
    MX: { pattern: /^\d{5}$/, examples: ['06082', '77520'] },
    MY: { pattern: /^(?!00000|99999)\d{5}$/, examples: ['50670', '43000'] },
    NL: { pattern: /^[1-9]\d{3} ?(?:[A-RT-Z][A-Z]|S[BCE-RT-Z])$/, examples: ['1234 AB', '1234AB'] },
    NO: { pattern: /^\d{4}$/, examples: ['0025', '6631'] },
    OM: { pattern: /^(?:PC )?\d{3}$/, examples: ['113', 'PC 113'] },
    PL: {
      pattern: /^\d{2}-\d{3}$/,
      examples: ['00-950', '31-015'],
      separator: '-',
      separatorPosition: 2,
    },
    PT: {
      pattern: /^\d{4}-\d{3}$/,
      examples: ['1250-096', '4000-322'],
      separator: '-',
      separatorPosition: 4,
    },
    QA: { pattern: /^\d*$/, examples: ['12345'] },
    RO: { pattern: /^\d{6}$/, examples: ['060274', '200716'] },
    RS: { pattern: /^\d{5,6}$/, examples: ['11000', '106314'] },
    RU: { pattern: /^\d{6}$/, examples: ['101000', '190000'] },
    SA: {
      pattern: /^\d{5}(?:-\d{4})?$/,
      examples: ['11564', '11564-1234'],
      separator: '-',
      separatorPosition: 5,
    },
    SE: {
      pattern: /^\d{3} \d{2}$/,
      examples: ['114 55', '411 01'],
      separator: ' ',
      separatorPosition: 3,
    },
    SI: { pattern: /^\d{4}$/, examples: ['1000', '2000'] },
    SK: { pattern: /^\d{3} ?\d{2}$/, examples: ['010 01', '01001'] },
    TH: { pattern: /^\d{5}$/, examples: ['10150', '50200'] },
    TR: { pattern: /^\d{5}$/, examples: ['06101', '34000'] },
    TW: { pattern: /^\d{3}(?:\d{2,3})?$/, examples: ['10603', '106001'] },
    UA: { pattern: /^\d{5}$/, examples: ['01001', '79000'] },
    US: {
      pattern: /^\d{5}(?:-\d{4})?$/,
      examples: ['95014', '22162-1010'],
      separator: '-',
      separatorPosition: 5,
    },
  }
}
